/**
 * `/experiments/$experimentId` — admin Experiment detail (#25).
 *
 * Layout:
 *   - Header           — name, status chip, start/stop buttons (enabled
 *                         per current status). Edit-name input is enabled
 *                         while DRAFT. Also surfaces a "Whole page" vs
 *                         "Block-level overrides" chip per issue #247 so
 *                         operators can see the experiment's kind at a
 *                         glance.
 *   - Variants section — table on md+, card list on xs. Each row shows
 *                         name, weight (editable as % when DRAFT), target
 *                         summary, delete button (DRAFT only).
 *   - Add variant      — opens a dialog. Issue #247 removed the
 *                         per-variant target-type radio: the dialog now
 *                         renders only the relevant picker for the
 *                         experiment's `targetKind`. PAGE_VERSION → the
 *                         version dropdown (page is fixed at
 *                         experiment.pageId). CONTENT_OVERRIDES → the
 *                         JSON editor (per-block picker is a follow-up).
 *
 * Responsive:
 *   - 360 / 768 / 1280 — table → card list at xs.
 */

import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArchiveIcon from "@mui/icons-material/Archive";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import ReplayIcon from "@mui/icons-material/Replay";
import StopIcon from "@mui/icons-material/Stop";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  type ContentOverridesJsonV1,
  type CreateVariantInput,
  type ExperimentDetailResponse,
  type ExperimentResponse,
  type ExperimentVariantResponse,
  type PageBlockWithEligibleVersions,
  type PageVersionResponse,
} from "@ottopilot/shared";
import {
  useArchiveExperiment,
  useCreateVariant,
  useDeleteVariant,
  useExperimentDetail,
  useResumeExperiment,
  useStartExperiment,
  useStopExperiment,
  useUpdateExperiment,
  useUpdateVariant,
} from "../hooks/useExperiments";
import { usePage } from "../hooks/usePageVersions";
import { useConfirmDialog } from "../components/ConfirmDialog";
import { useToast } from "../components/ToastProvider";
import { ExperimentStatusChip } from "../features/experiments/ExperimentStatusChip";
import { ExperimentMetricsSection } from "../features/experiments/ExperimentMetricsSection";
import { rebalanceWeights } from "../features/experiments/rebalanceWeights";

/**
 * Derive the "target type" label for a variant — the wire shape carries
 * one or the other, never both.
 */
function variantTargetLabel(variant: ExperimentVariantResponse): string {
  if (variant.pageVersionId) return "Page version";
  if (variant.contentOverridesJson) return "Content overrides";
  return "—";
}

function variantTargetSummary(variant: ExperimentVariantResponse): string {
  if (variant.pageVersionId) return variant.pageVersionId;
  if (variant.contentOverridesJson) {
    // Permissive narrowing — operators can paste arbitrary objects.
    const json = variant.contentOverridesJson;
    if (json && typeof json === "object" && "overrides" in json) {
      const overrides = (json as { overrides?: unknown }).overrides;
      const count = Array.isArray(overrides) ? overrides.length : 0;
      return `${count} override${count === 1 ? "" : "s"}`;
    }
    return "0 overrides";
  }
  return "—";
}

/**
 * Mirror of `PageDetailPage.tsx`'s `statusChipColor` — kept consistent so
 * the version-status chip in the variant-add picker uses the same
 * colour-mapping (PUBLISHED green, EXPERIMENT yellow) the operator sees
 * on the page detail page. Co-located here as a small inline helper
 * rather than lifted to a shared util because it's only two callers and
 * the mapping is tiny.
 */
function pageVersionStatusChipColor(
  status: PageVersionResponse["status"],
): "default" | "primary" | "success" | "warning" {
  switch (status) {
    case "DRAFT":
      return "primary";
    case "PUBLISHED":
      return "success";
    case "EXPERIMENT":
      return "warning";
    case "ARCHIVED":
      return "default";
    default:
      return "default";
  }
}

/**
 * Format an ISO timestamp as a short relative-time string. Mirrors the
 * `relativeTime` helper on `ExperimentsPage.tsx` so the "Published Xh ago"
 * suffix renders the same everywhere in the experiment surface.
 */
function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diffMs = Date.now() - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * Eligible-version set for a variant page-version target. Per issue #243
 * the server only accepts PUBLISHED or EXPERIMENT versions as variant
 * targets — DRAFT and ARCHIVED versions are rejected with
 * `VARIANT_TARGET_NOT_TRAFFIC_READY`. We mirror that filter client-side
 * by showing exactly `published ∪ experimentVersions` in the dropdown,
 * newest-eligible first (published before experiment versions, then by
 * descending versionNumber within experimentVersions).
 */
function eligibleVersions(
  page: { published: PageVersionResponse | null; experimentVersions: PageVersionResponse[] } | null,
): PageVersionResponse[] {
  if (!page) return [];
  const out: PageVersionResponse[] = [];
  if (page.published) out.push(page.published);
  for (const v of page.experimentVersions) out.push(v);
  return out;
}

function VariantWeightInput({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (next: number) => void;
  disabled: boolean;
}): ReactElement {
  return (
    <TextField
      type="number"
      size="small"
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const next = parseInt(e.target.value, 10);
        if (!Number.isNaN(next)) onChange(next);
      }}
      slotProps={{
        htmlInput: { min: 1, max: 100, step: 1, "data-testid": "variant-weight-input" },
      }}
      sx={{ width: 96 }}
    />
  );
}

/**
 * Issue #283 — per-block picker row state. One entry per block on the
 * page's effective PageVersion (`published` if present, else `latestDraft`,
 * sourced via the `blocksWithEligibleVersions` field on the page detail
 * response).
 *
 *   - `checked` — operator opted-in to overriding this block,
 *   - `selectedVersionId` — chosen ContentVersion id (must be in the
 *     row's `eligibleVersions`). Defaults to the first eligible version
 *     when the operator checks the row, mirroring the typical
 *     CMS-pattern "pick the most recent" affordance. Empty string when
 *     the row has no eligible versions yet.
 */
type BlockPickerRowState = { checked: boolean; selectedVersionId: string };

/**
 * Issue #283 — initialise per-block picker state. If `existingVariant` is
 * supplied (variant 2+ on an experiment that already has at least one
 * variant), pre-check the rows that variant had checked and seed each
 * row's version with that variant's selection. The operator can uncheck
 * any row to deviate; this is a soft UX nudge, not server-enforced.
 *
 * When `existingVariant` is null (first variant on the experiment), every
 * row defaults to unchecked + empty `selectedVersionId`. The
 * "select first eligible on check" affordance lands inside the row
 * checkbox handler, not here, so an unchecked row never carries a stale
 * selection from a previous picker open.
 */
function initialPickerState(
  blocks: PageBlockWithEligibleVersions[],
  existingVariant: ExperimentVariantResponse | null,
): Record<string, BlockPickerRowState> {
  // Parse the existing variant's contentOverridesJson defensively — it
  // may be the legacy array shape or a malformed object. We only need
  // the `{ contentItemId, contentVersionId }` pairs for the pre-check.
  const existingByItemId = new Map<string, string>();
  if (existingVariant && existingVariant.contentOverridesJson) {
    const json = existingVariant.contentOverridesJson;
    if (json && typeof json === "object" && "overrides" in json) {
      const overrides = (json as { overrides?: unknown }).overrides;
      if (Array.isArray(overrides)) {
        for (const o of overrides) {
          if (
            o &&
            typeof o === "object" &&
            "contentItemId" in o &&
            "contentVersionId" in o &&
            typeof (o as { contentItemId: unknown }).contentItemId === "string" &&
            typeof (o as { contentVersionId: unknown }).contentVersionId === "string"
          ) {
            existingByItemId.set(
              (o as { contentItemId: string }).contentItemId,
              (o as { contentVersionId: string }).contentVersionId,
            );
          }
        }
      }
    }
  }

  const out: Record<string, BlockPickerRowState> = {};
  for (const block of blocks) {
    const key = pickerRowKey(block);
    const seeded = existingByItemId.get(block.contentItemId);
    // Only pre-check when the seeded version is still in the row's
    // eligible set. Otherwise the pre-check would select a stale id the
    // server would later reject with VARIANT_TARGET_NOT_TRAFFIC_READY.
    if (seeded !== undefined && block.eligibleVersions.some((v) => v.id === seeded)) {
      out[key] = { checked: true, selectedVersionId: seeded };
    } else {
      out[key] = { checked: false, selectedVersionId: "" };
    }
  }
  return out;
}

/**
 * Stable per-row key. A ContentItem can appear multiple times in
 * `blocksOrderJson.blocks[]` (explicitly allowed by the schema), so we
 * combine `contentItemId + index` to disambiguate. Mirrors the same
 * `${id}-${index}` pattern used by `BlocksListEditor.rowKey` so the two
 * surfaces compose against identical block lists in the same order.
 */
function pickerRowKey(block: PageBlockWithEligibleVersions): string {
  return `${block.contentItemId}-${String(block.index)}`;
}

/**
 * Order the per-row eligible versions for display: PUBLISHED first (the
 * default render), then EXPERIMENT versions newest-first by
 * versionNumber. Mirrors the eligibility ordering on the PAGE_VERSION
 * picker (`eligibleVersions` higher up in this file) so the two pickers
 * feel consistent.
 */
function orderRowVersions(
  versions: PageBlockWithEligibleVersions["eligibleVersions"],
): PageBlockWithEligibleVersions["eligibleVersions"] {
  const published = versions.filter((v) => v.status === "PUBLISHED");
  const experiment = versions.filter((v) => v.status === "EXPERIMENT");
  experiment.sort((a, b) => b.versionNumber - a.versionNumber);
  return [...published, ...experiment];
}

function AddVariantDialog({
  open,
  experiment,
  existingVariants,
  onClose,
}: {
  open: boolean;
  experiment: ExperimentResponse;
  /**
   * Issue #283 — when the dialog opens for variant 2+ on a CONTENT_OVERRIDES
   * experiment that already has variants, we pre-check the rows the first
   * existing variant has selected. Empty when this is the first variant.
   */
  existingVariants: ExperimentVariantResponse[];
  onClose: () => void;
}): ReactElement {
  const create = useCreateVariant(experiment.id);
  const toast = useToast();
  const [name, setName] = useState("");
  const [weight, setWeight] = useState(50);
  // Issue #247: the per-variant target-type radio is gone — the kind
  // is determined by the experiment's `targetKind`. The dialog still
  // tracks the picker state appropriate for the current kind.
  const isContentOverrides = experiment.targetKind === "CONTENT_OVERRIDES";
  // Issue #283: site-scoped (pageId null) CONTENT_OVERRIDES experiments
  // need a separate page-picker UX before the per-block editor can render
  // — deferred to a follow-up. The page-scoped branch is the focus of
  // this slice.
  const isSiteScopedContentOverrides = isContentOverrides && experiment.pageId === null;
  const [pageVersionId, setPageVersionId] = useState("");
  const [pickerState, setPickerState] = useState<Record<string, BlockPickerRowState>>({});
  const [serverError, setServerError] = useState<string | null>(null);

  // Issue #247: PAGE_VERSION experiments now REQUIRE `pageId` at create
  // time (API: `PAGE_ID_REQUIRED_FOR_PAGE_VERSION_EXPERIMENT`), so the
  // dialog can always rely on `experiment.pageId` being non-null for
  // the version dropdown — no site-scoped two-step picker needed.
  // CONTENT_OVERRIDES experiments may be site-scoped (pageId null);
  // we skip the page-version fetch entirely on that branch.
  // Issue #283: page-scoped CONTENT_OVERRIDES experiments also need the
  // page detail (for `blocksWithEligibleVersions`), so we fetch whenever
  // `experiment.pageId` is present.
  const targetPageId = experiment.pageId;
  const pageDetail = usePage(targetPageId ?? undefined);
  const versions = useMemo(() => eligibleVersions(pageDetail.data ?? null), [pageDetail.data]);
  const versionsLoading = Boolean(targetPageId) && pageDetail.isPending;
  const versionsEmpty = Boolean(targetPageId) && !pageDetail.isPending && versions.length === 0;

  // Issue #283 — blocks-with-eligible-versions for the per-block picker.
  // Read defensively (`?? []`) because the field is optional on the wire
  // and older API builds may not surface it yet.
  const blocksWithEligibleVersions = useMemo<PageBlockWithEligibleVersions[]>(
    () => pageDetail.data?.blocksWithEligibleVersions ?? [],
    [pageDetail.data],
  );
  const blocksLoading = isContentOverrides && Boolean(targetPageId) && pageDetail.isPending;

  // Issue #283 — re-seed picker state once per dialog open, as soon as
  // the blocks list resolves. Once seeded, operator edits
  // (check/uncheck/version pick) are the source of truth — re-seeding
  // on every render from a fresh TanStack Query data reference would
  // clobber the operator's clicks.
  //
  // The "first existing variant" snapshot is taken at seed-time so the
  // pre-check is deterministic for the dialog session. If a concurrent
  // variant create lands while the dialog is open the operator's
  // current selections aren't disturbed; the next open re-evaluates.
  //
  // Source of pre-check: the FIRST existing variant. Matches the "soft
  // nudge" UX per issue #283 — operator sees what variant 1 picked,
  // can uncheck rows to deviate. Not server-enforced.
  const [pickerSeededForOpen, setPickerSeededForOpen] = useState(false);
  useEffect(() => {
    if (!open) {
      // Reset the seeded-once latch on close so the NEXT open re-seeds.
      if (pickerSeededForOpen) setPickerSeededForOpen(false);
      return;
    }
    if (!isContentOverrides) return;
    if (isSiteScopedContentOverrides) return;
    if (pickerSeededForOpen) return;
    // Wait for the page detail (and therefore blocksWithEligibleVersions)
    // to finish loading before seeding — otherwise the seed runs against
    // an empty array and the operator's later state would be clobbered
    // by the post-load re-render.
    if (blocksLoading) return;
    const firstExistingVariant = existingVariants[0] ?? null;
    setPickerState(initialPickerState(blocksWithEligibleVersions, firstExistingVariant));
    setPickerSeededForOpen(true);
  }, [
    open,
    pickerSeededForOpen,
    isContentOverrides,
    isSiteScopedContentOverrides,
    blocksLoading,
    blocksWithEligibleVersions,
    existingVariants,
  ]);

  // Keep the page-version selection consistent with the current page.
  // If the eligible set changes (e.g. cache invalidation after a
  // publish), drop a stale selection so the operator can't submit an
  // id that no longer appears in the dropdown.
  useEffect(() => {
    if (!pageVersionId) return;
    if (versionsLoading) return;
    const stillEligible = versions.some((v) => v.id === pageVersionId);
    if (!stillEligible) setPageVersionId("");
  }, [pageVersionId, versions, versionsLoading]);

  const reset = () => {
    setName("");
    setWeight(50);
    setPageVersionId("");
    setPickerState({});
    setServerError(null);
  };

  const handleClose = () => {
    if (create.isPending) return;
    reset();
    onClose();
  };

  const handleTogglePickerRow = (block: PageBlockWithEligibleVersions, checked: boolean) => {
    const key = pickerRowKey(block);
    setPickerState((prev) => {
      const next = { ...prev };
      const current = next[key] ?? { checked: false, selectedVersionId: "" };
      if (checked) {
        // Auto-select the first eligible version on opt-in so the
        // operator isn't forced to pick a default before submitting. The
        // version dropdown is ordered PUBLISHED-first → newest EXPERIMENT
        // (matches the PAGE_VERSION picker's mental model).
        const ordered = orderRowVersions(block.eligibleVersions);
        const fallback = ordered[0]?.id ?? "";
        next[key] = {
          checked: true,
          selectedVersionId: current.selectedVersionId || fallback,
        };
      } else {
        next[key] = { checked: false, selectedVersionId: "" };
      }
      return next;
    });
  };

  const handleSelectPickerVersion = (block: PageBlockWithEligibleVersions, versionId: string) => {
    const key = pickerRowKey(block);
    setPickerState((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] ?? { checked: true, selectedVersionId: "" }),
        selectedVersionId: versionId,
      },
    }));
  };

  /**
   * Issue #283 — derive the `contentOverridesJson` payload from the
   * current picker state. Filters to checked rows that have a non-empty
   * selected version id, then maps each to a
   * `{ contentItemId, contentVersionId }` pair. Returns the full
   * schemaVersion-1 envelope ready for `CreateVariantInput`.
   */
  const buildPickerOverrides = (): ContentOverridesJsonV1 => {
    const overrides: { contentItemId: string; contentVersionId: string }[] = [];
    for (const block of blocksWithEligibleVersions) {
      const key = pickerRowKey(block);
      const row = pickerState[key];
      if (!row || !row.checked || !row.selectedVersionId) continue;
      overrides.push({
        contentItemId: block.contentItemId,
        contentVersionId: row.selectedVersionId,
      });
    }
    return { schemaVersion: 1, overrides };
  };

  const handleSubmit = () => {
    setServerError(null);
    if (!name.trim()) return;
    if (isSiteScopedContentOverrides) return;

    let input: CreateVariantInput;
    if (!isContentOverrides) {
      const pvid = pageVersionId.trim();
      if (!pvid) return;
      input = { name: name.trim(), weight, pageVersionId: pvid };
    } else {
      // Page-scoped CONTENT_OVERRIDES — submit always succeeds locally
      // because the picker can only select traffic-ready versions from
      // the row's `eligibleVersions` (matches the server's
      // `assertContentOverridesOnSite` rule). The server still validates
      // defence-in-depth.
      input = {
        name: name.trim(),
        weight,
        contentOverridesJson: buildPickerOverrides(),
      };
    }

    create.mutate(input, {
      onSuccess: () => {
        toast.showSuccess("Variant added.");
        reset();
        onClose();
      },
      onError: (err) => {
        // `err` is an `ApiClientError` (mutation hook is parameterised
        // on it). We render the precise server code so the operator can
        // see e.g. `PAGE_VERSION_NOT_FOUND_OR_CROSS_SITE` and fix the id.
        setServerError(`${err.code}: ${err.message}`);
      },
    });
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>Add variant</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            autoFocus
            data-testid="variant-add-name-input"
          />
          <TextField
            label="Weight (%)"
            type="number"
            value={weight}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n)) setWeight(n);
            }}
            slotProps={{ htmlInput: { min: 1, max: 100 } }}
            sx={{ maxWidth: 160 }}
            data-testid="variant-add-weight-input"
          />
          {/* Issue #247: target-type radio removed. The dialog renders
              only the picker that matches the experiment's `targetKind`.
              Issue #283: CONTENT_OVERRIDES now uses a structured per-block
              picker instead of the raw JSON textarea. Site-scoped
              CONTENT_OVERRIDES experiments are deferred to a follow-up. */}
          {!isContentOverrides ? (
            <FormControl fullWidth disabled={!targetPageId || versionsLoading || versionsEmpty}>
              <InputLabel id="variant-add-page-version-select-label">Page version</InputLabel>
              <Select
                labelId="variant-add-page-version-select-label"
                label="Page version"
                value={pageVersionId}
                onChange={(e) => setPageVersionId(e.target.value)}
                data-testid="variant-add-page-version-select"
                renderValue={(value) => {
                  if (!value) return "";
                  const v = versions.find((x) => x.id === value);
                  if (!v) return value;
                  return `v${v.versionNumber} · ${v.status}`;
                }}
              >
                {versions.map((v) => (
                  <MenuItem
                    key={v.id}
                    value={v.id}
                    data-testid={`variant-add-page-version-option-${v.id}`}
                  >
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        v{v.versionNumber}
                      </Typography>
                      <Chip
                        label={v.status}
                        size="small"
                        color={pageVersionStatusChipColor(v.status)}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                        Published {relativeTime(v.publishedAt)}
                      </Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText
                // The helper text owns three states: loading, empty
                // (with a link to the page detail so the operator can
                // publish a draft for experiment use), and the default
                // "only PUBLISHED/EXPERIMENT are eligible" reminder.
                data-testid="variant-add-page-version-helper"
              >
                {versionsLoading ? (
                  "Loading versions…"
                ) : versionsEmpty && targetPageId ? (
                  <>
                    This page has no PUBLISHED or EXPERIMENT versions yet — publish a draft for
                    experiment use first.{" "}
                    <Link
                      to="/sites/$siteId/pages/$pageId"
                      params={{ siteId: experiment.siteId, pageId: targetPageId }}
                    >
                      Open page
                    </Link>
                  </>
                ) : !targetPageId ? (
                  "This PAGE_VERSION experiment is missing a pageId — recreate the experiment with a page selected."
                ) : (
                  "Only PUBLISHED and EXPERIMENT versions are eligible. DRAFT and ARCHIVED versions are rejected."
                )}
              </FormHelperText>
            </FormControl>
          ) : isSiteScopedContentOverrides ? (
            <Alert severity="info" data-testid="variant-add-content-overrides-site-scoped-alert">
              Site-scoped CONTENT_OVERRIDES experiments need a page picker for the per-block editor
              — coming in a follow-up.
            </Alert>
          ) : (
            <BlockOverridesPicker
              blocks={blocksWithEligibleVersions}
              blocksLoading={blocksLoading}
              pickerState={pickerState}
              onToggleRow={handleTogglePickerRow}
              onSelectVersion={handleSelectPickerVersion}
              siteId={experiment.siteId}
              pageId={targetPageId ?? ""}
            />
          )}
          {serverError ? <Alert severity="error">{serverError}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={create.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={create.isPending || !name.trim() || isSiteScopedContentOverrides}
          data-testid="variant-add-submit"
        >
          Add
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Issue #283 — per-block picker for page-scoped CONTENT_OVERRIDES
 * variants. Renders one row per block in the page's effective
 * PageVersion (`published` if present, else `latestDraft`), with a
 * checkbox to opt-in and a version Select pre-filtered to the block's
 * traffic-ready ContentVersions.
 *
 * Three empty states owned by this component:
 *   - `blocksLoading` — page detail fetch is in flight.
 *   - `blocks.length === 0` — page exists but its source PageVersion has
 *     no blocks yet; we point at the page detail so the operator can add
 *     some.
 *   - `eligibleVersions.length === 0` on a row — the ContentItem has no
 *     PUBLISHED/EXPERIMENT versions yet; we disable the row's checkbox
 *     and version Select and surface an inline hint.
 *
 * Co-located with the dialog rather than promoted to its own file because
 * it has no other call sites and the rendering is straightforward.
 */
function BlockOverridesPicker({
  blocks,
  blocksLoading,
  pickerState,
  onToggleRow,
  onSelectVersion,
  siteId,
  pageId,
}: {
  blocks: PageBlockWithEligibleVersions[];
  blocksLoading: boolean;
  pickerState: Record<string, BlockPickerRowState>;
  onToggleRow: (block: PageBlockWithEligibleVersions, checked: boolean) => void;
  onSelectVersion: (block: PageBlockWithEligibleVersions, versionId: string) => void;
  siteId: string;
  pageId: string;
}): ReactElement {
  if (blocksLoading) {
    return (
      <Box
        sx={{ display: "flex", justifyContent: "center", py: 3 }}
        data-testid="variant-add-block-overrides-loading"
      >
        <CircularProgress size={24} />
      </Box>
    );
  }
  if (blocks.length === 0) {
    return (
      <Alert severity="info" data-testid="variant-add-block-overrides-empty-alert">
        This page has no blocks yet — add blocks to the page before composing a CONTENT_OVERRIDES
        variant.{" "}
        {pageId ? (
          <Link to="/sites/$siteId/pages/$pageId" params={{ siteId, pageId }}>
            Open page
          </Link>
        ) : null}
      </Alert>
    );
  }
  return (
    <Stack spacing={1} data-testid="variant-add-block-overrides-list">
      <Typography variant="caption" color="text.secondary">
        Pick which blocks this variant overrides. The renderer uses the page&apos;s currently
        published versions for any unchecked block.
      </Typography>
      <Paper variant="outlined">
        <Stack divider={<Divider />}>
          {blocks.map((block) => {
            const key = pickerRowKey(block);
            const row = pickerState[key] ?? { checked: false, selectedVersionId: "" };
            const orderedVersions = orderRowVersions(block.eligibleVersions);
            const hasEligible = orderedVersions.length > 0;
            const isOrphan = block.contentItem === null;
            const rowDisabled = !hasEligible || isOrphan;
            const label = isOrphan
              ? `Unknown content item — ${block.contentItemId}`
              : (block.contentItem?.name ?? block.contentItemId);
            const blockTypeChip = block.contentItem?.blockType ?? null;
            return (
              <Stack
                key={key}
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ p: 2 }}
                data-testid={`variant-add-block-override-row-${block.contentItemId}`}
              >
                <FormControlLabel
                  sx={{ flex: 1, minWidth: 0, mr: 0 }}
                  control={
                    <Checkbox
                      checked={row.checked}
                      disabled={rowDisabled}
                      onChange={(e) => onToggleRow(block, e.target.checked)}
                      slotProps={{
                        input: {
                          "data-testid": `variant-add-block-override-checkbox-${block.contentItemId}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                  }
                  label={
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: "wrap" }}
                      useFlexGap
                    >
                      {blockTypeChip ? (
                        <Chip
                          label={blockTypeChip}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      ) : null}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500 }}
                        color={isOrphan ? "text.disabled" : undefined}
                      >
                        {label}
                      </Typography>
                    </Stack>
                  }
                />
                <FormControl
                  size="small"
                  disabled={!row.checked || rowDisabled}
                  sx={{ minWidth: { xs: "100%", sm: 240 } }}
                >
                  <InputLabel
                    id={`variant-add-block-override-version-label-${block.contentItemId}`}
                  >
                    Version
                  </InputLabel>
                  <Select
                    labelId={`variant-add-block-override-version-label-${block.contentItemId}`}
                    label="Version"
                    value={row.selectedVersionId}
                    onChange={(e) => onSelectVersion(block, e.target.value)}
                    data-testid={`variant-add-block-override-version-select-${block.contentItemId}`}
                    renderValue={(value) => {
                      if (!value) return "";
                      const v = orderedVersions.find((x) => x.id === value);
                      if (!v) return value;
                      return `v${v.versionNumber} · ${v.status}`;
                    }}
                  >
                    {orderedVersions.map((v) => (
                      <MenuItem
                        key={v.id}
                        value={v.id}
                        data-testid={`variant-add-block-override-version-option-${block.contentItemId}-${v.id}`}
                      >
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ width: "100%" }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            v{v.versionNumber}
                          </Typography>
                          <Chip
                            label={v.status}
                            size="small"
                            color={pageVersionStatusChipColor(v.status)}
                          />
                          <Typography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                            Published {relativeTime(v.publishedAt)}
                          </Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </Select>
                  {!hasEligible && !isOrphan ? (
                    <FormHelperText>
                      No PUBLISHED or EXPERIMENT versions for this block yet.
                    </FormHelperText>
                  ) : null}
                  {isOrphan ? (
                    <FormHelperText>
                      This block&apos;s content item is missing on this site.
                    </FormHelperText>
                  ) : null}
                </FormControl>
              </Stack>
            );
          })}
        </Stack>
      </Paper>
    </Stack>
  );
}

function ExperimentDetailContent({ detail }: { detail: ExperimentDetailResponse }): ReactElement {
  const { experiment, variants } = detail;
  const isDraft = experiment.status === "DRAFT";
  const isRunning = experiment.status === "RUNNING";
  // Issue #253: lifecycle additions. `isCompleted` toggles Resume +
  // Archive; `isArchived` makes the page read-only.
  const isCompleted = experiment.status === "COMPLETED";
  const isArchived = experiment.status === "ARCHIVED";
  const canArchive = isDraft || isCompleted;

  const update = useUpdateExperiment(experiment.id);
  const updateVariant = useUpdateVariant(experiment.id);
  const deleteVariant = useDeleteVariant(experiment.id);
  const start = useStartExperiment(experiment.id);
  const stop = useStopExperiment(experiment.id);
  const resume = useResumeExperiment(experiment.id);
  const archive = useArchiveExperiment(experiment.id);
  const navigate = useNavigate();
  const toast = useToast();
  // Issue #255 AC2 — shared confirmation dialog for archive-experiment
  // and delete-variant. Mounted once at the top of the JSX so a single
  // dialog instance services both call sites.
  const { confirm, dialogElement: confirmDialog } = useConfirmDialog();

  const [nameDraft, setNameDraft] = useState(experiment.name);
  const [addOpen, setAddOpen] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  // Issue #249: track the auto-fix click in flight separately from the
  // single-variant `updateVariant.isPending` flag. Sequential PATCHes
  // flip `isPending` on/off between awaits, which would make the button
  // spinner flicker. A dedicated state captures "the rebalance is in
  // progress" cleanly.
  const [rebalanceInFlight, setRebalanceInFlight] = useState(false);

  useEffect(() => {
    setNameDraft(experiment.name);
  }, [experiment.name]);

  const totalWeight = useMemo(() => variants.reduce((sum, v) => sum + v.weight, 0), [variants]);

  const handleSaveName = () => {
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === experiment.name) return;
    update.mutate(
      { name: trimmed },
      {
        onSuccess: () => toast.showSuccess("Experiment updated."),
        onError: (err) => toast.showError(err.message || "Could not update experiment."),
      },
    );
  };

  const handleStart = () => {
    setServerError(null);
    start.mutate(undefined, {
      onSuccess: () => toast.showSuccess("Experiment started."),
      onError: (err) => {
        // The mutation hook is parameterised on `ApiClientError`, so
        // `err.code` is always present. We surface the precise code in
        // the inline alert (the operator's reference for "weights don't
        // sum" / "needs at least 2 variants") instead of a toast so it
        // stays readable beside the buttons.
        setServerError(`${err.code}: ${err.message}`);
      },
    });
  };

  const handleStop = () => {
    setServerError(null);
    stop.mutate(undefined, {
      onSuccess: () => toast.showSuccess("Experiment stopped."),
      onError: (err) => {
        setServerError(`${err.code}: ${err.message}`);
      },
    });
  };

  // Issue #253: COMPLETED → RUNNING. The API re-runs the same validation
  // Start does, so the precise server codes (variant target, lockguard,
  // weights) surface in the inline alert.
  const handleResume = () => {
    setServerError(null);
    resume.mutate(undefined, {
      onSuccess: () => toast.showSuccess("Experiment resumed."),
      onError: (err) => {
        setServerError(`${err.code}: ${err.message}`);
      },
    });
  };

  // Issue #253: archive needs explicit confirmation. Issue #255 AC2
  // migrated this from `window.confirm` to the MUI ConfirmDialog so the
  // styling matches the rest of the admin. On success we navigate back
  // to /experiments — the detail page on an archived row would be
  // mostly-read-only and confusing.
  const handleArchive = async () => {
    setServerError(null);
    const ok = await confirm({
      title: "Archive this experiment?",
      body: "Archive this experiment? It will be hidden from the list by default. There is no unarchive action.",
      confirmLabel: "Archive",
    });
    if (!ok) return;
    archive.mutate(undefined, {
      onSuccess: () => {
        toast.showSuccess("Experiment archived.");
        void navigate({ to: "/experiments" });
      },
      onError: (err) => {
        setServerError(`${err.code}: ${err.message}`);
      },
    });
  };

  const handleWeightChange = (variantId: string, weight: number) => {
    updateVariant.mutate(
      { variantId, input: { weight } },
      {
        onError: (err) => toast.showError(err.message || "Could not update variant weight."),
      },
    );
  };

  // Issue #255 AC2 — delete-variant gets a confirmation prompt. The
  // delete is irreversible (the variant row is destroyed, not soft-
  // archived) so a misclick was previously a real risk. The button is
  // already gated to DRAFT experiments only; the confirm protects the
  // gated path.
  const handleDeleteVariant = async (variantId: string) => {
    const ok = await confirm({
      title: "Delete this variant?",
      body: "Delete this variant? This cannot be undone.",
      confirmLabel: "Delete",
    });
    if (!ok) return;
    deleteVariant.mutate(variantId, {
      onSuccess: () => toast.showSuccess("Variant deleted."),
      onError: (err) => toast.showError(err.message || "Could not delete variant."),
    });
  };

  // Issue #249: rebalance variant weights so they sum to exactly 100.
  // The algorithm lives in `features/experiments/rebalanceWeights.ts` —
  // we keep the side-effecting "issue a PATCH per row that changed"
  // glue here. PATCHes are sequential (`await` in a `for` loop) on
  // purpose: parallel writes risk reordering audit-log rows and racing
  // the server's lockGuard. Typical experiments have 2–5 variants, so
  // the latency cost is small.
  const handleRebalance = async () => {
    const currentWeights = variants.map((v) => v.weight);
    const next = rebalanceWeights(currentWeights);
    if (next === null) {
      toast.showError("Can't auto-fix — adjust manually.");
      return;
    }
    setRebalanceInFlight(true);
    try {
      for (let i = 0; i < variants.length; i++) {
        const variant = variants[i]!;
        const nextWeight = next[i]!;
        if (variant.weight === nextWeight) continue;
        await updateVariant.mutateAsync({
          variantId: variant.id,
          input: { weight: nextWeight },
        });
      }
      toast.showSuccess("Weights rebalanced to 100%.");
    } catch (err) {
      // ApiClientError carries a `.message` field — surface it via the
      // toast machinery and let the operator retry by re-clicking. We
      // don't roll back partial PATCHes because the server is already
      // permissive about temporary invalid sums in DRAFT.
      const message =
        err instanceof Error && err.message ? err.message : "Could not rebalance weights.";
      toast.showError(message);
    } finally {
      setRebalanceInFlight(false);
    }
  };

  return (
    <Stack spacing={3} data-testid="experiment-detail">
      {confirmDialog}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "flex-start" }}
      >
        <Box sx={{ flex: 1 }}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ mb: 1 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography variant="h2" data-testid="experiment-detail-name">
              {experiment.name}
            </Typography>
            <ExperimentStatusChip status={experiment.status} size="medium" />
            {/* Issue #247: surface the experiment's target kind so
                operators can see at a glance whether they're looking
                at a whole-page or block-level overrides experiment. */}
            <Chip
              label={
                experiment.targetKind === "PAGE_VERSION" ? "Whole page" : "Block-level overrides"
              }
              size="medium"
              variant="outlined"
              color="primary"
              data-testid={`experiment-detail-target-kind-${experiment.targetKind}`}
            />
          </Stack>
          {isDraft ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ maxWidth: 480 }}>
              <TextField
                label="Name"
                size="small"
                value={nameDraft}
                onChange={(e) => setNameDraft(e.target.value)}
                fullWidth
                data-testid="experiment-detail-name-input"
              />
              <Button
                variant="outlined"
                disabled={
                  update.isPending || !nameDraft.trim() || nameDraft.trim() === experiment.name
                }
                onClick={handleSaveName}
                data-testid="experiment-detail-name-save"
              >
                Save
              </Button>
            </Stack>
          ) : null}
        </Box>
        {/* Issue #253: ARCHIVED experiments are read-only — render no
            lifecycle buttons at all. The Alert below surfaces the state. */}
        {!isArchived ? (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Tooltip
              title={
                !isDraft
                  ? "Only draft experiments can be started."
                  : variants.length < 2
                    ? "Add at least 2 variants to start."
                    : totalWeight !== 100
                      ? // Issue #249: surface the current sum explicitly so the
                        // operator knows exactly how far off they are without
                        // having to count weights manually.
                        `Weights must total 100% (currently ${totalWeight}%).`
                      : "Start experiment"
              }
            >
              <span>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<PlayArrowIcon />}
                  disabled={
                    !isDraft || variants.length < 2 || totalWeight !== 100 || start.isPending
                  }
                  onClick={handleStart}
                  data-testid="experiment-detail-start-button"
                >
                  Start
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={isRunning ? "Stop experiment" : "Only running experiments can be stopped."}
            >
              <span>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<StopIcon />}
                  disabled={!isRunning || stop.isPending}
                  onClick={handleStop}
                  data-testid="experiment-detail-stop-button"
                >
                  Stop
                </Button>
              </span>
            </Tooltip>
            {/* Issue #253: Resume is only visible on COMPLETED. The server
                re-runs the same validation Start runs, so the precise
                code surfaces inline if anything drifted (target archived,
                lockguard, weights) between stop and resume. */}
            {isCompleted ? (
              <Tooltip title="Resume experiment">
                <span>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<ReplayIcon />}
                    disabled={resume.isPending}
                    onClick={handleResume}
                    data-testid="experiment-detail-resume-button"
                  >
                    Resume experiment
                  </Button>
                </span>
              </Tooltip>
            ) : null}
            {/* Issue #253: Archive is visible on DRAFT or COMPLETED. RUNNING
                experiments must be stopped first — the API would refuse
                with `EXPERIMENT_RUNNING_CANNOT_ARCHIVE`. */}
            {canArchive ? (
              <Tooltip title="Archive experiment">
                <span>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<ArchiveIcon />}
                    disabled={archive.isPending}
                    onClick={() => {
                      void handleArchive();
                    }}
                    data-testid="experiment-detail-archive-button"
                  >
                    Archive
                  </Button>
                </span>
              </Tooltip>
            ) : null}
          </Stack>
        ) : null}
      </Stack>

      {/* Issue #253: read-only banner on ARCHIVED. Other interactive
          surfaces (add variant, weight inputs, delete variant) are
          already gated on `isDraft` / `isRunning`, so they naturally
          disable here too. */}
      {isArchived ? (
        <Alert severity="info" data-testid="experiment-detail-archived-alert">
          This experiment is archived.
        </Alert>
      ) : null}

      {serverError ? (
        <Alert severity="error" data-testid="experiment-detail-error">
          {serverError}
        </Alert>
      ) : null}

      {/* Issue #249: loud, persistent banner when the variant weights
          don't sum to 100. Only shown in DRAFT (RUNNING experiments can
          have legitimately-skewed totals once variants have been added
          and rebalanced server-side at start). The "Fix to 100%" action
          rebalances via sequential variant PATCHes. */}
      {isDraft && variants.length > 0 && totalWeight !== 100 ? (
        <Alert
          severity="warning"
          data-testid="experiment-detail-weight-sum-banner"
          action={
            <Button
              color="inherit"
              size="small"
              variant="outlined"
              onClick={() => {
                void handleRebalance();
              }}
              disabled={rebalanceInFlight}
              startIcon={
                rebalanceInFlight ? <CircularProgress size={16} color="inherit" /> : undefined
              }
              data-testid="experiment-detail-weight-sum-fix-button"
            >
              Fix to 100%
            </Button>
          }
        >
          Variant weights total {totalWeight}% — they must sum to 100% before the experiment can
          start.
        </Alert>
      ) : null}

      <Divider />

      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h3">Variants</Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography
              variant="body2"
              color={totalWeight === 100 ? "success.main" : "text.secondary"}
              data-testid="experiment-detail-weight-sum"
            >
              Weights sum: {totalWeight}/100
            </Typography>
            <Button
              variant="outlined"
              disabled={!isDraft}
              onClick={() => setAddOpen(true)}
              data-testid="experiment-detail-add-variant-button"
            >
              Add variant
            </Button>
          </Stack>
        </Stack>

        {/* Table view (md+) */}
        <Paper variant="outlined" sx={{ display: { xs: "none", md: "block" } }}>
          <TableContainer>
            <Table aria-label="Variants" data-testid="experiment-detail-variants-table">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Weight (%)</TableCell>
                  <TableCell>Target</TableCell>
                  <TableCell>Detail</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variants.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      align="center"
                      sx={{ py: 4 }}
                      data-testid="experiment-detail-variants-empty"
                    >
                      <Typography variant="body2" color="text.secondary">
                        No variants yet. Add at least 2 to start the experiment.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  variants.map((v) => (
                    <TableRow
                      key={v.id}
                      data-testid="experiment-detail-variant-row"
                      data-variant-id={v.id}
                    >
                      <TableCell>{v.name}</TableCell>
                      <TableCell>
                        <VariantWeightInput
                          value={v.weight}
                          disabled={!isDraft || updateVariant.isPending}
                          onChange={(next) => handleWeightChange(v.id, next)}
                        />
                      </TableCell>
                      <TableCell>{variantTargetLabel(v)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                          {variantTargetSummary(v)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={isDraft ? "Delete variant" : "Only draft variants can be deleted."}
                        >
                          <span>
                            <IconButton
                              aria-label="Delete variant"
                              disabled={!isDraft || deleteVariant.isPending}
                              onClick={() => {
                                void handleDeleteVariant(v.id);
                              }}
                              data-testid="experiment-detail-variant-delete"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Card list (xs/sm) */}
        <Stack
          spacing={1.5}
          sx={{ display: { xs: "flex", md: "none" } }}
          data-testid="experiment-detail-variants-cards"
        >
          {variants.length === 0 ? (
            <Paper variant="outlined" sx={{ py: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No variants yet.
              </Typography>
            </Paper>
          ) : (
            variants.map((v) => (
              <Card key={v.id} variant="outlined">
                <CardContent>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    sx={{ mb: 1 }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {v.name}
                    </Typography>
                    <Tooltip title={isDraft ? "Delete variant" : "DRAFT only"}>
                      <span>
                        <IconButton
                          aria-label="Delete variant"
                          disabled={!isDraft || deleteVariant.isPending}
                          onClick={() => {
                            void handleDeleteVariant(v.id);
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    {/* Mirror the desktop view's numeric weight input so
                        the operator sees the same control on mobile and
                        we don't have to maintain two parallel weight UIs. */}
                    <VariantWeightInput
                      value={v.weight}
                      disabled={!isDraft || updateVariant.isPending}
                      onChange={(next) => handleWeightChange(v.id, next)}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {variantTargetLabel(v)}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", color: "text.secondary" }}
                  >
                    {variantTargetSummary(v)}
                  </Typography>
                </CardContent>
              </Card>
            ))
          )}
        </Stack>
      </Box>

      {/* Issue #27: metrics dashboard. Hidden in DRAFT (no data yet);
          loads its own data via TanStack Query and surfaces loading +
          error states inline. */}
      <ExperimentMetricsSection experimentId={experiment.id} status={experiment.status} />

      <AddVariantDialog
        open={addOpen}
        experiment={experiment}
        existingVariants={variants}
        onClose={() => setAddOpen(false)}
      />
    </Stack>
  );
}

export function ExperimentDetailPage(): ReactElement {
  // `strict: false` returns a partial params object regardless of where
  // the component is mounted in the route tree. The production mount
  // sits under the pathless `authenticated` route (full id is
  // `/authenticated/experiments/$experimentId`); component tests mount
  // it on a flat tree at `/experiments/$experimentId`. Both yield the
  // same `experimentId` param key.
  const params = useParams({ strict: false });
  const experimentId = typeof params.experimentId === "string" ? params.experimentId : "";
  const detail = useExperimentDetail(experimentId);
  const navigate = useNavigate();

  if (detail.isPending) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (detail.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" data-testid="experiment-detail-load-error">
          {detail.error.message}
        </Alert>
        <Button
          sx={{ mt: 2 }}
          onClick={() => {
            void navigate({ to: "/experiments" });
          }}
        >
          Back to experiments
        </Button>
      </Box>
    );
  }
  return <ExperimentDetailContent detail={detail.data} />;
}
